import { __decorate } from "tslib";
import { Component, Emit, Prop, Provide, Vue } from "vue-property-decorator";
import { modalMixins } from "@/mixins/modal.js";
import { priceStyle, totalPrice } from "@/Pages/People/Customer/TreatmentPlan/utils";
import { mapActions } from "vuex";
import User from "@/components/User.vue";
import Money from "@/components/Money.vue";
import CustomSwitch from "@/components/CustomSwitch.vue";
import ToothPopover from "@/components/Tooth/ToothPopover.vue";
import ConfirmableButton from "@/components/ConfirmableButton.vue";
import PresetTreatmentBrowser from "@/components/PTBrowserModal.vue";
import PlanTable from "./PlanTable.vue";
import TreatmentCard from "./TreatmentCard.vue";
import LabelInput from "@/Pages/People/Customer/TreatmentPlan/LabelInput.vue";
import AddTreatmentModal from "@/Pages/Easy/AddTreatmentModal.vue";
import Stages from "@/Pages/People/Customer/TreatmentPlan/Stages.vue";
let Plan = class Plan extends Vue {
    expanded;
    plan;
    printMode;
    customerId;
    editMode;
    groupByTooth = false;
    stage = null;
    tooth = null;
    patch;
    remove;
    openModal;
    toggleEditMode() {
        return !this.editMode;
    }
    get browserModalId() {
        return 'PrintPlanModal-' + this.plan._id;
    }
    get addTreatmentModalId() {
        return 'AddTreatmentModal-' + this.plan._id;
    }
    get checkBoxId() {
        return 'checkbox-' + this.plan._id;
    }
    get selected() {
        return this.expanded || this.printMode;
    }
    get planLabel() {
        return this.plan.label;
    }
    set planLabel(val) {
        this.savePlan({ ...this.plan, label: val });
    }
    get planPreviewLabel() {
        let createdAt = this.$moment(this.plan.createdAt).calendar();
        if (!this.planLabel)
            return createdAt;
        return `${this.planLabel} (${createdAt})`;
    }
    savePlan(plan) {
        return this.patch([plan._id, plan, {}]);
    }
    currentPlan() {
        return this.$deepCopy(this.plan);
    }
    addTooth(teeth) {
        let tooth = teeth[0];
        if (!tooth)
            return;
        let plan = this.currentPlan();
        if (plan.teeth.findIndex(el => el.tooth === tooth) === -1) {
            plan.teeth.unshift({ tooth: tooth, stages: { 1: [] } });
            this.savePlan(plan);
        }
        this.$root.$emit("bv::hide::popover");
    }
    openBrowser(stage, tooth) {
        this.stage = stage || null;
        this.tooth = tooth || null;
        this.openModal({ id: this.addTreatmentModalId, model: {} });
    }
    addTreatments(results) {
        let plan = this.currentPlan();
        results.forEach(result => {
            let { tooth, stage, treatment } = result;
            const toothIdx = plan.teeth.findIndex((el) => el.tooth === tooth);
            if (toothIdx === -1) {
                plan.teeth.push({
                    tooth: tooth,
                    stages: {
                        [stage]: [
                            {
                                treatment: treatment,
                                treatmentId: treatment._id,
                                stage: stage,
                                priority: 0
                            },
                        ],
                    },
                });
            }
            else {
                if (!plan.teeth[toothIdx].stages)
                    plan.teeth[toothIdx].stages = {};
                if (!plan.teeth[toothIdx].stages[stage])
                    plan.teeth[toothIdx].stages[stage] = [];
                plan.teeth[toothIdx].stages[stage].push({
                    stage: stage,
                    treatment: treatment,
                    treatmentId: treatment._id,
                    priority: plan.teeth[toothIdx].stages[stage].length
                });
            }
        });
        plan.teeth = plan.teeth.sort((item1, item2) => (item1.tooth || 0) > (item2.tooth || 0) ? 1 : -1);
        this.savePlan(plan);
    }
    addTreatment(item, isPreset) {
        let stage = this.stage || 1;
        let tooth = this.tooth;
        let plan = this.currentPlan();
        const treatments = isPreset ? item.treatments : [item];
        treatments.forEach(treatment => {
            const toothIdx = plan.teeth.findIndex((el) => el.tooth === tooth);
            if (toothIdx === -1) {
                plan.teeth.unshift({
                    tooth: null,
                    stages: {
                        1: [
                            {
                                treatment,
                                treatmentId: treatment._id,
                                stage: 1,
                                priority: 0
                            },
                        ],
                    },
                });
                return;
            }
            plan.teeth[toothIdx].stages[stage].push({
                stage: stage,
                treatment,
                treatmentId: treatment._id,
                priority: plan.teeth[toothIdx].stages[stage].length
            });
        });
        return plan;
    }
    useTreatment(treatment) {
        this.savePlan(this.addTreatment(treatment));
    }
    usePreset(preset) {
        this.savePlan(this.addTreatment(preset, true));
    }
    removePlan(id) {
        this.remove(id).then(() => {
            this.$toasted.global.success({
                message: this.$t("Successfully removed"),
            });
        });
    }
    print() {
        let data = {
            plan: this.plan,
            groupedMode: this.groupByTooth,
            groupedByTooth: this.planTeeth(this.plan.teeth),
            groupedByColor: this.groupedByColorPlanTeeth(this.plan.teeth)
        };
        const { TreatmentPlan } = this.$FeathersVuex;
        new TreatmentPlan({
            model: data,
            _id: "temp",
        });
        this.openModal({ id: "PrintPlanModal" });
    }
    // plan-table start
    unwrap(stages) {
        stages = this.$copy(stages);
        return Object.entries(stages).reduce((result, [key, values]) => {
            if (!values.length)
                return result;
            values[0].rowspan = values.length;
            values.forEach((el) => el.stage = key);
            return [...result, ...values];
        }, []);
    }
    groupByColor(teeth) {
        let stages = {};
        // collect plan by stages from teeth
        teeth.forEach((tooth) => {
            Object.keys(tooth.stages).forEach(stageId => {
                stages[stageId] = this.$mergeArrays(stages[stageId], tooth.stages[stageId].map((el) => ({
                    ...el,
                    tooth: tooth.tooth
                })));
            });
        });
        let result = {}; // final result container
        let commonStages = {}; // container for colorless stages (they are common for all other colors)
        Object.keys(stages).forEach(stage => {
            // group  treatments with same color
            let groupedByColor = this.$groupBy(stages[stage], el => el.color || "");
            Object.keys(groupedByColor).forEach(stageColor => {
                // group same treatments with same color
                let groupedByTreatment = this.$groupBy(groupedByColor[stageColor], el => el.treatment._id);
                // union same treatments with same color
                let groupedStages = Object.values(groupedByTreatment).map((items) => {
                    return items.reduce((res, item) => {
                        res.quantity++;
                        res.teeth.push(item.tooth);
                        return res;
                    }, { ...items[0], quantity: 0, teeth: [] });
                });
                // If it has color add it to result
                // otherwise we add it to commonStages
                if (stageColor.length > 0) {
                    if (!result[stageColor]) {
                        result[stageColor] = { [stage]: groupedStages };
                        return;
                    }
                    if (!result[stageColor][stage]) {
                        result[stageColor][stage] = groupedStages;
                        return;
                    }
                    result[stageColor][stage].push(...groupedStages);
                }
                else { //it is stage without color
                    if (!commonStages[stage]) {
                        commonStages[stage] = groupedStages;
                        return;
                    }
                    commonStages[stage].push(...groupedStages);
                }
            });
        });
        // append commonStages to each color
        Object.keys(commonStages).forEach(stage => {
            Object.keys(result).forEach(existingColor => {
                if (result[existingColor][stage])
                    result[existingColor][stage].push(...commonStages[stage]);
                else
                    result[existingColor][stage] = commonStages[stage];
            });
        });
        return Object.values(result);
    }
    groupByTreatment(teeth) {
        let stages = {};
        // collect plan by stages from teeth
        teeth.forEach((tooth) => {
            Object.keys(tooth.stages).forEach(stageId => {
                stages[stageId] = this.$mergeArrays(stages[stageId], tooth.stages[stageId].map((el) => ({
                    ...el,
                    tooth: tooth.tooth
                })));
            });
        });
        // iterate each stage
        Object.keys(stages).forEach(stage => {
            // group treatments of stage by treatment
            let groupedByTreatment = this.$groupBy(stages[stage], el => el.treatment._id);
            // empty container for grouped stage treatments
            stages[stage] = [];
            Object.values(groupedByTreatment).map((treatmentGroup) => {
                // group same treatments by color
                let groupedByColor = this.$groupBy(treatmentGroup, el => el.color);
                // union same treatments with same color and save in stages object
                stages[stage].push(...Object.values(groupedByColor).map((items) => {
                    return items.reduce((res, item) => {
                        res.quantity++;
                        res.teeth.push(item.tooth);
                        return res;
                    }, { ...items[0], quantity: 0, teeth: [] });
                }));
            });
        });
        return stages;
    }
    planTeeth(teeth) {
        if (!teeth)
            return [];
        return [this.unwrap(this.groupByTreatment([...teeth]))];
    }
    getTeeth() {
        return this.plan.teeth.reduce((result, tooth) => {
            if (tooth.tooth)
                result.push(tooth.tooth);
            return result;
        }, []);
    }
    totalPrice(items) {
        return totalPrice(items);
    }
    priceStyle(items) {
        return priceStyle(items);
    }
    groupedByColorPlanTeeth(teeth) {
        if (!teeth)
            return [];
        let test = this.$deepCopy(teeth);
        return this.groupByColor(test).map((values) => this.unwrap(values));
    }
};
__decorate([
    Prop({ type: Boolean })
], Plan.prototype, "expanded", void 0);
__decorate([
    Prop({ type: Object, required: true })
], Plan.prototype, "plan", void 0);
__decorate([
    Prop({ type: Boolean })
], Plan.prototype, "printMode", void 0);
__decorate([
    Prop({ type: String })
], Plan.prototype, "customerId", void 0);
__decorate([
    Prop({ type: Boolean })
], Plan.prototype, "editMode", void 0);
__decorate([
    Emit('update:editMode')
], Plan.prototype, "toggleEditMode", null);
__decorate([
    Provide('openBrowser')
], Plan.prototype, "openBrowser", null);
Plan = __decorate([
    Component({
        components: {
            Stages,
            LabelInput,
            PresetTreatmentBrowser,
            User,
            Money,
            CustomSwitch,
            PlanTable,
            ToothPopover,
            ConfirmableButton,
            TreatmentCard,
            AddTreatmentModal,
        },
        mixins: [modalMixins],
        methods: {
            ...mapActions("treatment-plan", ["patch", "remove"]),
        },
        computed: {}
    })
], Plan);
export default Plan;
